<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import cortexSettingsAPI from 'dashboard/api/cortexSettings';
import cortexModelRouteProfilesAPI from 'dashboard/api/cortexModelRouteProfiles';
import cortexModelRoutesAPI from 'dashboard/api/cortexModelRoutes';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';

const { t } = useI18n();
const profiles = ref([]);
const selectedProfileId = ref(null);
const activeProfile = ref('');
const routes = ref([]);
const connections = ref([]);
const modelOptionsByProvider = ref({});
const embeddingOptionsByProvider = ref({});
const profileName = ref('');
const isLoading = ref(false);
const isSaving = ref(false);
const createDialog = ref(null);
const applyDialog = ref(null);
const updateDialog = ref(null);
const deleteDialog = ref(null);
const REQUIRED_WORKLOADS = [
  'realtime_response',
  'background',
  'media',
  'transcription',
  'embeddings',
];

const profileTitle = profile =>
  profile.key
    ? t(`CORTEX_MODELS.ROUTES.PROFILES.${profile.key}.TITLE`)
    : profile.name;

const profileDescription = profile =>
  profile.key
    ? t(`CORTEX_MODELS.ROUTES.PROFILES.${profile.key}.DESCRIPTION`)
    : t(
        profile.owner_scope === 'platform'
          ? 'CORTEX_MODELS.ROUTES.PROFILES.PLATFORM_DESCRIPTION'
          : 'CORTEX_MODELS.ROUTES.PROFILES.ACCOUNT_DESCRIPTION'
      );

const profileOptions = computed(() =>
  profiles.value.map(profile => ({
    value: profile.id,
    label: profileTitle(profile),
    description: profileDescription(profile),
  }))
);

const selectedProfile = computed(() =>
  profiles.value.find(profile => profile.id === selectedProfileId.value)
);

const workloadTitle = workload =>
  t(`CORTEX_MODELS.ROUTES.WORKLOADS.${workload}.TITLE`);

const isProfileInUse = profile =>
  activeProfile.value === `${profile.owner_scope}:${profile.id}` ||
  activeProfile.value === profile.key;

const connectionIsOperational = connection =>
  connection.status === 'active' &&
  connection.credentials?.some(
    credential =>
      credential.status === 'active' && credential.api_key_configured
  );

const operationalConnections = computed(() =>
  connections.value.filter(connection => connectionIsOperational(connection))
);

const targetIsAvailable = target =>
  connections.value.some(
    connection =>
      connection.id === target.provider_connection_id &&
      (connection.owner_scope === 'account' || connection.adopted) &&
      connectionIsOperational(connection)
  );

const profileIsComplete = profile => {
  const profileRoutes = profile?.routes || [];
  const requiredRoutesReady = REQUIRED_WORKLOADS.every(workload => {
    const route = profileRoutes.find(item => item.workload === workload);
    return route?.targets?.length > 0 && route.targets.every(targetIsAvailable);
  });

  return (
    requiredRoutesReady &&
    profileRoutes.every(route => route.targets.every(targetIsAvailable))
  );
};

const canSaveSelectedProfile = computed(
  () =>
    selectedProfile.value?.editable && profileIsComplete(selectedProfile.value)
);

const connectionOptions = computed(() =>
  connections.value.map(connection => ({
    value: connection.id,
    label: `${connection.name} · ${connection.provider_name}`,
    disabled: !connectionIsOperational(connection),
  }))
);

const connectionForTarget = target =>
  connections.value.find(
    connection => connection.id === Number(target.provider_connection_id)
  );

const modelOptionsFor = (workload, target) => {
  const connection = connectionForTarget(target);
  if (!connection) return [];
  const source =
    workload === 'embeddings'
      ? embeddingOptionsByProvider.value
      : modelOptionsByProvider.value;
  const options =
    workload === 'embeddings'
      ? source[connection.provider] || []
      : connection.model_options || source[connection.provider] || [];
  return options.map(option => ({
    value: option.value,
    label: option.name || option.label,
  }));
};

const selectActiveProfile = modelRouteProfile => {
  const scopedMatch = modelRouteProfile?.match(/^(platform|account):(\d+)$/);
  const profile = scopedMatch
    ? profiles.value.find(
        item =>
          item.owner_scope === scopedMatch[1] &&
          String(item.id) === scopedMatch[2]
      )
    : profiles.value.find(item => item.key === modelRouteProfile);
  selectedProfileId.value = profile?.id || profiles.value[0]?.id || null;
};

const loadData = async () => {
  isLoading.value = true;
  try {
    const [{ data: settings }, { data: profileData }, { data: routeData }] =
      await Promise.all([
        cortexSettingsAPI.get(),
        cortexModelRouteProfilesAPI.get(),
        cortexModelRoutesAPI.get(),
      ]);
    profiles.value = profileData;
    routes.value = routeData;
    connections.value = settings.model?.provider_connections || [];
    modelOptionsByProvider.value =
      settings.model?.model_options_by_provider || {};
    embeddingOptionsByProvider.value =
      settings.model?.embedding_model_options_by_provider || {};
    activeProfile.value = settings.model?.model_route_profile || '';
    selectActiveProfile(activeProfile.value);
  } catch (error) {
    useAlert(t('CORTEX_MODELS.ERRORS.LOAD'));
  } finally {
    isLoading.value = false;
  }
};

const createProfile = async () => {
  isSaving.value = true;
  try {
    const { data } = await cortexModelRouteProfilesAPI.create({
      model_route_profile: { name: profileName.value },
    });
    profiles.value.push(data);
    selectedProfileId.value = data.id;
    createDialog.value?.close();
    useAlert(t('CORTEX_MODELS.ROUTES.PROFILES.CREATED'));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.SAVE')
    );
  } finally {
    isSaving.value = false;
  }
};

const applyProfile = async () => {
  isSaving.value = true;
  try {
    await cortexModelRouteProfilesAPI.apply(selectedProfile.value.id);
    activeProfile.value = `${selectedProfile.value.owner_scope}:${selectedProfile.value.id}`;
    applyDialog.value?.close();
    useAlert(t('CORTEX_MODELS.ROUTES.PROFILES.APPLIED'));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.SAVE')
    );
  } finally {
    isSaving.value = false;
  }
};

const updateProfile = async () => {
  isSaving.value = true;
  try {
    const { data } = await cortexModelRouteProfilesAPI.update(
      selectedProfile.value.id,
      {
        model_route_profile: {
          name: selectedProfile.value.name,
          configuration: {
            routes: selectedProfile.value.routes.reduce(
              (configuration, route) => ({
                ...configuration,
                [route.workload]: {
                  targets: route.targets.map(target => ({
                    provider_connection_id: Number(
                      target.provider_connection_id
                    ),
                    model: target.model,
                  })),
                },
              }),
              {}
            ),
          },
        },
      }
    );
    const index = profiles.value.findIndex(item => item.id === data.id);
    profiles.value[index] = data;
    updateDialog.value?.close();
    useAlert(t('CORTEX_MODELS.ROUTES.PROFILES.UPDATED'));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.SAVE')
    );
  } finally {
    isSaving.value = false;
  }
};

const deleteProfile = async () => {
  isSaving.value = true;
  try {
    await cortexModelRouteProfilesAPI.delete(selectedProfile.value.id);
    profiles.value = profiles.value.filter(
      item => item.id !== selectedProfile.value.id
    );
    selectedProfileId.value = profiles.value[0]?.id || null;
    deleteDialog.value?.close();
    useAlert(t('CORTEX_MODELS.ROUTES.PROFILES.DELETED'));
  } catch (error) {
    useAlert(
      error.response?.data?.errors?.[0] || t('CORTEX_MODELS.ERRORS.DELETE')
    );
  } finally {
    isSaving.value = false;
  }
};

const openCreateDialog = () => {
  profileName.value = '';
  createDialog.value?.open();
};

const setTargetConnection = (route, target, connectionId) => {
  target.provider_connection_id = connectionId;
  target.model = modelOptionsFor(route.workload, target)[0]?.value || '';
};

const addTarget = route => {
  const connection = operationalConnections.value[0];
  if (!connection || (route.workload === 'embeddings' && route.targets.length))
    return;

  const target = { provider_connection_id: connection.id, model: '' };
  route.targets.push(target);
  target.model = modelOptionsFor(route.workload, target)[0]?.value || '';
};

const removeTarget = (route, index) => route.targets.splice(index, 1);

onMounted(loadData);
</script>

<template>
  <div class="flex flex-col gap-5" :class="{ 'opacity-60': isLoading }">
    <div>
      <h4 class="text-heading-3 text-n-slate-12">
        {{ t('CORTEX_MODELS.ROUTES.PROFILES.PAGE_TITLE') }}
      </h4>
      <p class="mt-1 text-body-small text-n-slate-11">
        {{ t('CORTEX_MODELS.ROUTES.PROFILES.PAGE_DESCRIPTION') }}
      </p>
    </div>

    <div class="p-4 border rounded-lg border-n-weak bg-n-alpha-1">
      <div class="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <WithLabel
          name="model-route-profile"
          :label="t('CORTEX_MODELS.ROUTES.PROFILES.LABEL')"
          :help-message="t('CORTEX_MODELS.ROUTES.PROFILES.DESCRIPTION')"
        >
          <AdaptiveSelect
            v-model="selectedProfileId"
            :options="profileOptions"
          />
        </WithLabel>
        <NextButton
          icon="i-lucide-bookmark-plus"
          :label="t('CORTEX_MODELS.ROUTES.PROFILES.CREATE')"
          :disabled="!operationalConnections.length"
          @click="openCreateDialog"
        />
      </div>
      <p
        v-if="!operationalConnections.length"
        class="mt-3 text-label-small leading-4 text-n-slate-11"
      >
        {{ t('CORTEX_MODELS.ROUTES.PROFILES.PRIVATE_REQUIREMENT') }}
      </p>

      <div
        v-if="selectedProfile"
        class="flex flex-col gap-4 pt-4 mt-4 border-t border-n-weak"
      >
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ profileTitle(selectedProfile) }}
          </span>
          <Label
            :label="
              selectedProfile.editable
                ? t('CORTEX_MODELS.ROUTES.PROFILES.PRIVATE')
                : t('CORTEX_MODELS.ROUTES.PROFILES.PLATFORM')
            "
            :color="selectedProfile.editable ? 'slate' : 'teal'"
            compact
          />
          <Label
            v-if="isProfileInUse(selectedProfile)"
            :label="t('CORTEX_MODELS.ROUTES.PROFILES.IN_USE')"
            color="teal"
            compact
          />
        </div>
        <p class="text-body-small text-n-slate-11">
          {{ profileDescription(selectedProfile) }}
        </p>

        <NextInput
          v-if="selectedProfile.editable"
          v-model="selectedProfile.name"
          :label="t('CORTEX_MODELS.ROUTES.PROFILES.NAME')"
        />

        <div class="overflow-hidden border rounded-lg border-n-weak">
          <div class="px-4 py-3 border-b border-n-weak bg-n-alpha-2">
            <h5 class="text-sm font-medium text-n-slate-12">
              {{ t('CORTEX_MODELS.ROUTES.PROFILES.CONFIGURATION_TITLE') }}
            </h5>
            <p class="mt-1 text-label-small leading-4 text-n-slate-11">
              {{
                selectedProfile.editable
                  ? t(
                      'CORTEX_MODELS.ROUTES.PROFILES.CONFIGURATION_EDITABLE_DESCRIPTION'
                    )
                  : t('CORTEX_MODELS.ROUTES.PROFILES.CONFIGURATION_DESCRIPTION')
              }}
            </p>
          </div>
          <div
            v-for="route in selectedProfile.routes"
            :key="route.workload"
            class="px-4 py-3 border-b last:border-b-0 border-n-weak"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ workloadTitle(route.workload) }}
            </p>
            <div v-if="route.targets.length" class="flex flex-col gap-3 mt-3">
              <div
                v-for="target in route.targets"
                :key="`${route.workload}-${target.provider_connection_id}-${target.model}`"
                class="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] md:items-start"
              >
                <WithLabel
                  :name="`profile-connection-${route.workload}-${target.position}`"
                  :label="t('CORTEX_MODELS.ROUTES.CONNECTION')"
                >
                  <AdaptiveSelect
                    v-if="selectedProfile.editable"
                    :model-value="target.provider_connection_id"
                    :options="connectionOptions"
                    @update:model-value="
                      setTargetConnection(route, target, $event)
                    "
                  />
                  <span v-else class="text-sm text-n-slate-11">
                    {{ target.connection_name }}
                  </span>
                </WithLabel>
                <WithLabel
                  :name="`profile-model-${route.workload}-${target.position}`"
                  :label="t('CORTEX_MODELS.ROUTES.MODEL')"
                >
                  <AdaptiveSelect
                    v-if="selectedProfile.editable"
                    v-model="target.model"
                    :options="modelOptionsFor(route.workload, target)"
                  />
                  <code v-else class="text-xs text-n-slate-12">{{
                    target.model
                  }}</code>
                </WithLabel>
                <NextButton
                  v-if="selectedProfile.editable"
                  slate
                  ghost
                  class="md:mt-6"
                  icon="i-lucide-trash-2"
                  :label="t('CORTEX_MODELS.ACTIONS.REMOVE_DESTINATION')"
                  @click="removeTarget(route, route.targets.indexOf(target))"
                />
              </div>
            </div>
            <p v-else class="mt-2 text-label-small text-n-slate-10">
              {{ t('CORTEX_MODELS.ROUTES.PROFILES.NO_DESTINATIONS') }}
            </p>
            <NextButton
              v-if="
                selectedProfile.editable &&
                (route.workload !== 'embeddings' || !route.targets.length)
              "
              class="mt-3"
              slate
              outline
              icon="i-lucide-plus"
              :label="t('CORTEX_MODELS.ROUTES.PROFILES.ADD_DESTINATION')"
              @click="addTarget(route)"
            />
          </div>
        </div>

        <div class="flex flex-wrap justify-end gap-3">
          <NextButton
            v-if="selectedProfile.editable"
            slate
            ghost
            icon="i-lucide-trash-2"
            :label="t('CORTEX_MODELS.ROUTES.PROFILES.DELETE')"
            @click="deleteDialog?.open()"
          />
          <NextButton
            v-if="selectedProfile.editable"
            slate
            outline
            icon="i-lucide-refresh-cw"
            :label="t('CORTEX_MODELS.ROUTES.PROFILES.SAVE_CONFIGURATION')"
            :disabled="!canSaveSelectedProfile"
            @click="updateDialog?.open()"
          />
          <NextButton
            icon="i-lucide-rotate-ccw"
            :label="t('CORTEX_MODELS.ROUTES.PROFILES.APPLY')"
            @click="applyDialog?.open()"
          />
        </div>
      </div>
    </div>

    <Dialog
      ref="createDialog"
      :title="t('CORTEX_MODELS.ROUTES.PROFILES.CREATE_TITLE')"
      :description="t('CORTEX_MODELS.ROUTES.PROFILES.CREATE_DESCRIPTION')"
      :confirm-button-label="t('CORTEX_MODELS.ROUTES.PROFILES.CREATE_CONFIRM')"
      :cancel-button-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
      confirm-button-icon="i-lucide-save"
      :is-loading="isSaving"
      :disable-confirm-button="!profileName.trim()"
      @confirm="createProfile"
    >
      <NextInput
        v-model="profileName"
        :label="t('CORTEX_MODELS.ROUTES.PROFILES.NAME')"
        :placeholder="t('CORTEX_MODELS.ROUTES.PROFILES.NAME_PLACEHOLDER')"
      />
    </Dialog>

    <Dialog
      ref="applyDialog"
      type="alert"
      :title="t('CORTEX_MODELS.ROUTES.PROFILES.APPLY_TITLE')"
      :description="t('CORTEX_MODELS.ROUTES.PROFILES.APPLY_CONFIRMATION')"
      :confirm-button-label="t('CORTEX_MODELS.ROUTES.PROFILES.APPLY')"
      :cancel-button-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
      :is-loading="isSaving"
      @confirm="applyProfile"
    />

    <Dialog
      ref="updateDialog"
      type="alert"
      :title="t('CORTEX_MODELS.ROUTES.PROFILES.SAVE_CONFIGURATION_TITLE')"
      :description="
        t('CORTEX_MODELS.ROUTES.PROFILES.SAVE_CONFIGURATION_CONFIRMATION')
      "
      :confirm-button-label="
        t('CORTEX_MODELS.ROUTES.PROFILES.SAVE_CONFIGURATION')
      "
      :cancel-button-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
      :is-loading="isSaving"
      @confirm="updateProfile"
    />

    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="t('CORTEX_MODELS.ROUTES.PROFILES.DELETE_TITLE')"
      :description="t('CORTEX_MODELS.ROUTES.PROFILES.DELETE_CONFIRMATION')"
      :confirm-button-label="t('CORTEX_MODELS.ROUTES.PROFILES.DELETE')"
      :cancel-button-label="t('CORTEX_MODELS.ACTIONS.CANCEL')"
      :is-loading="isSaving"
      @confirm="deleteProfile"
    />
  </div>
</template>
